import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { Menu, X, ArrowRight, Code, Layout, Database, ChevronDown, Mail, Phone, MapPin, Linkedin, Github, Twitter, ExternalLink, Users, ClipboardList, Package, BarChart3, CheckCircle2, Layers, Zap, Shield } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animated Section Component
const AnimatedSection = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled ? "bg-white/90 backdrop-blur-md border-b border-smoke" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-20 lg:h-24">
            {/* Logo */}
            <button 
              onClick={() => scrollToSection("hero")} 
              className="font-display text-xl lg:text-2xl font-bold tracking-tight"
              data-testid="logo-btn"
            >
              JERVES<span className="text-charcoal">.NET</span>
            </button>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-10">
              <button onClick={() => scrollToSection("services")} className="nav-link" data-testid="nav-services">
                Services
              </button>
              <button onClick={() => scrollToSection("products")} className="nav-link" data-testid="nav-products">
                Products
              </button>
              <button onClick={() => scrollToSection("about")} className="nav-link" data-testid="nav-about">
                About
              </button>
              <button onClick={() => scrollToSection("portfolio")} className="nav-link" data-testid="nav-portfolio">
                Work
              </button>
              <button onClick={() => scrollToSection("contact")} className="nav-link" data-testid="nav-contact">
                Contact
              </button>
            </div>

            {/* CTA Button */}
            <button
              onClick={() => scrollToSection("contact")}
              className="hidden lg:block btn-primary"
              data-testid="nav-get-started"
            >
              <span>Get Started</span>
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileMenuOpen ? "active" : ""}`}>
        <button
          onClick={() => setMobileMenuOpen(false)}
          className="absolute top-6 right-6 p-2"
          data-testid="mobile-menu-close"
        >
          <X size={24} strokeWidth={1.5} />
        </button>
        <button onClick={() => scrollToSection("services")} className="mobile-menu-link" data-testid="mobile-nav-services">
          Services
        </button>
        <button onClick={() => scrollToSection("products")} className="mobile-menu-link" data-testid="mobile-nav-products">
          Products
        </button>
        <button onClick={() => scrollToSection("about")} className="mobile-menu-link" data-testid="mobile-nav-about">
          About
        </button>
        <button onClick={() => scrollToSection("portfolio")} className="mobile-menu-link" data-testid="mobile-nav-portfolio">
          Work
        </button>
        <button onClick={() => scrollToSection("contact")} className="mobile-menu-link" data-testid="mobile-nav-contact">
          Contact
        </button>
      </div>
    </>
  );
};

// Hero Section
const HeroSection = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden bg-white">
      {/* Background Image with Parallax */}
      <motion.div style={{ y }} className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-white/90 z-10" />
        <img
          src="https://images.unsplash.com/photo-1649608118930-70b0a62cdf62?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Abstract architectural curves"
          className="w-full h-full object-cover opacity-30"
        />
      </motion.div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 grid-pattern z-10" />

      <div className="relative z-20 max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24 pt-32 pb-24">
        <motion.div style={{ opacity }} className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Left Content */}
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hero-subtitle text-slate mb-6"
            >
              IT & Software Solutions
            </motion.p>
            
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="hero-text font-display font-extrabold text-onyx mb-8"
            >
              Crafting
              <br />
              <span className="font-accent font-normal italic">Digital</span>
              <br />
              Excellence
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="text-lg text-slate max-w-md mb-12 leading-relaxed"
            >
              We architect premium software solutions and bespoke web experiences 
              for visionary businesses worldwide.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="flex flex-wrap gap-4"
            >
              <button 
                onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
                className="btn-primary flex items-center gap-3"
                data-testid="hero-start-project"
              >
                <span>Start a Project</span>
                <ArrowRight size={16} strokeWidth={1.5} />
              </button>
              <button 
                onClick={() => document.getElementById("products")?.scrollIntoView({ behavior: "smooth" })}
                className="btn-secondary"
                data-testid="hero-view-products"
              >
                <span>Our Products</span>
              </button>
            </motion.div>
          </div>

          {/* Right Stats */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="hidden lg:block"
          >
            <div className="grid grid-cols-2 gap-12">
              <div className="border-l border-smoke pl-8">
                <p className="stat-number">150+</p>
                <p className="stat-label">Projects Delivered</p>
              </div>
              <div className="border-l border-smoke pl-8">
                <p className="stat-number">8+</p>
                <p className="stat-label">Years Experience</p>
              </div>
              <div className="border-l border-smoke pl-8">
                <p className="stat-number">98%</p>
                <p className="stat-label">Client Satisfaction</p>
              </div>
              <div className="border-l border-smoke pl-8">
                <p className="stat-number">24/7</p>
                <p className="stat-label">Support Available</p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        >
          <span className="text-xs uppercase tracking-widest text-slate">Scroll</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <ChevronDown size={20} strokeWidth={1} className="text-slate" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      number: "01",
      icon: <Layout size={32} strokeWidth={1} />,
      title: "Web Development",
      description: "Custom-built websites and web applications that combine stunning aesthetics with flawless functionality. From corporate sites to complex platforms.",
    },
    {
      number: "02",
      icon: <Code size={32} strokeWidth={1} />,
      title: "Software Solutions",
      description: "Tailored software development to streamline your operations. Enterprise systems, automation tools, and custom applications built for scale.",
    },
    {
      number: "03",
      icon: <Database size={32} strokeWidth={1} />,
      title: "IT Consulting",
      description: "Strategic technology consulting to optimize your digital infrastructure. Architecture design, tech stack selection, and digital transformation.",
    },
  ];

  return (
    <section id="services" className="py-32 lg:py-48 bg-ash">
      <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24">
        <AnimatedSection>
          <p className="section-subtitle">What We Do</p>
          <h2 className="section-title font-display">
            Precision Engineering
            <br />
            <span className="font-accent font-normal italic">for Digital Success</span>
          </h2>
        </AnimatedSection>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
          {services.map((service, index) => (
            <AnimatedSection key={index} delay={index * 0.2}>
              <div className="service-card h-full" data-testid={`service-card-${index}`}>
                <p className="service-number">{service.number}</p>
                <div className="mb-6 text-charcoal">{service.icon}</div>
                <h3 className="font-display text-2xl font-bold mb-4">{service.title}</h3>
                <p className="text-slate leading-relaxed">{service.description}</p>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

// Products Section - NEW
const ProductsSection = () => {
  const products = [
    {
      name: "Jarves Project",
      tagline: "Streamline Your Project Management",
      description: "A comprehensive project management platform designed to help teams manage projects, track orders, and collaborate seamlessly. Built for efficiency and scale.",
      url: "https://project.jarves.net",
      image: "https://images.unsplash.com/photo-1531403009284-440f080d1e12?crop=entropy&cs=srgb&fm=jpg&q=85",
      features: [
        { icon: <ClipboardList size={18} strokeWidth={1.5} />, text: "Project Applications" },
        { icon: <Package size={18} strokeWidth={1.5} />, text: "Sample Requests" },
        { icon: <BarChart3 size={18} strokeWidth={1.5} />, text: "Order Tracking" },
        { icon: <Users size={18} strokeWidth={1.5} />, text: "Team Collaboration" },
      ],
      highlights: [
        "Streamlined project workflow",
        "Real-time collaboration",
        "Comprehensive tracking",
        "24/7 cloud access"
      ],
      status: "Live",
    },
  ];

  return (
    <section id="products" className="py-32 lg:py-48 bg-white">
      <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24">
        <AnimatedSection>
          <p className="section-subtitle">Our Products</p>
          <h2 className="section-title font-display">
            SaaS Solutions
            <br />
            <span className="font-accent font-normal italic">Built by Jerves</span>
          </h2>
          <p className="text-slate text-lg max-w-2xl mt-6 leading-relaxed">
            We don't just build for clients – we build products that solve real business problems.
            Explore our suite of professional software solutions.
          </p>
        </AnimatedSection>

        <div className="mt-16 space-y-24">
          {products.map((product, index) => (
            <AnimatedSection key={index} delay={0.2}>
              <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-testid={`product-${index}`}>
                {/* Product Image */}
                <div className="relative group">
                  <div className="aspect-[16/10] overflow-hidden bg-ash">
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  {/* Status Badge */}
                  <div className="absolute top-4 left-4 bg-onyx text-vapor px-4 py-2 text-xs uppercase tracking-widest font-bold flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                    {product.status}
                  </div>
                </div>

                {/* Product Info */}
                <div>
                  <h3 className="font-display text-3xl lg:text-4xl font-bold mb-3">{product.name}</h3>
                  <p className="text-charcoal text-lg mb-6 font-medium">{product.tagline}</p>
                  <p className="text-slate leading-relaxed mb-8">{product.description}</p>
                  
                  {/* Features Grid */}
                  <div className="grid grid-cols-2 gap-4 mb-8">
                    {product.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-3 text-slate">
                        <span className="text-charcoal">{feature.icon}</span>
                        <span className="text-sm">{feature.text}</span>
                      </div>
                    ))}
                  </div>

                  {/* Highlights */}
                  <div className="border-t border-smoke pt-6 mb-8">
                    <div className="grid grid-cols-2 gap-3">
                      {product.highlights.map((highlight, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-slate">
                          <CheckCircle2 size={14} strokeWidth={1.5} className="text-charcoal" />
                          {highlight}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA */}
                  <a 
                    href={product.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary inline-flex items-center gap-3"
                    data-testid={`product-cta-${index}`}
                  >
                    <span>Visit {product.name}</span>
                    <ExternalLink size={16} strokeWidth={1.5} />
                  </a>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>

        {/* More Products Coming Soon */}
        <AnimatedSection delay={0.4}>
          <div className="mt-24 text-center">
            <div className="inline-block border border-smoke p-12 lg:p-16">
              <Layers size={40} strokeWidth={1} className="mx-auto mb-6 text-charcoal" />
              <h4 className="font-display text-xl font-bold mb-3">More Products Coming Soon</h4>
              <p className="text-slate text-sm max-w-md mx-auto">
                We're constantly innovating. Stay tuned for more powerful SaaS solutions 
                designed to transform your business operations.
              </p>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="py-32 lg:py-48 bg-ash">
      <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Image */}
          <AnimatedSection>
            <div className="relative aspect-[4/3] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1580315362297-5be36f0de025?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Minimalist workspace"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 right-0 bg-onyx text-vapor p-8 lg:p-12">
                <p className="stat-number text-vapor">2016</p>
                <p className="stat-label text-vapor/70">Established</p>
              </div>
            </div>
          </AnimatedSection>

          {/* Content */}
          <AnimatedSection delay={0.2}>
            <p className="section-subtitle">About Us</p>
            <h2 className="section-title font-display">
              The Architects of
              <br />
              <span className="font-accent font-normal italic">Digital Legacy</span>
            </h2>
            
            <p className="text-slate text-lg leading-relaxed mb-8">
              At Jerves.net, we don't just build software – we craft digital experiences 
              that elevate businesses. Our philosophy centers on precision, innovation, 
              and an unwavering commitment to excellence.
            </p>
            
            <p className="text-slate leading-relaxed mb-12">
              Every project begins with a deep understanding of your vision. We combine 
              cutting-edge technology with timeless design principles to deliver solutions 
              that stand the test of time. From startups to enterprises, we've helped 
              businesses across industries transform their digital presence.
            </p>

            {/* Core Values */}
            <div className="grid grid-cols-3 gap-6 mb-12">
              <div className="text-center">
                <Zap size={24} strokeWidth={1} className="mx-auto mb-3 text-charcoal" />
                <p className="text-xs uppercase tracking-widest text-slate">Innovation</p>
              </div>
              <div className="text-center">
                <Shield size={24} strokeWidth={1} className="mx-auto mb-3 text-charcoal" />
                <p className="text-xs uppercase tracking-widest text-slate">Reliability</p>
              </div>
              <div className="text-center">
                <CheckCircle2 size={24} strokeWidth={1} className="mx-auto mb-3 text-charcoal" />
                <p className="text-xs uppercase tracking-widest text-slate">Excellence</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-8">
              <div>
                <p className="stat-number">50+</p>
                <p className="stat-label">Happy Clients</p>
              </div>
              <div>
                <p className="stat-number">15</p>
                <p className="stat-label">Team Members</p>
              </div>
              <div>
                <p className="stat-number">12</p>
                <p className="stat-label">Countries Served</p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// Portfolio Section - UPDATED with project.jarves.net
const PortfolioSection = () => {
  const projects = [
    {
      image: "https://images.unsplash.com/photo-1531403009284-440f080d1e12?crop=entropy&cs=srgb&fm=jpg&q=85",
      category: "SaaS Product",
      title: "Jarves Project",
      description: "Comprehensive project management platform with team collaboration",
      url: "https://project.jarves.net",
      featured: true,
    },
    {
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&q=85",
      category: "Web Development",
      title: "Analytics Dashboard",
      description: "Real-time business intelligence platform",
      url: null,
      featured: false,
    },
    {
      image: "https://images.unsplash.com/photo-1572829850501-87b66a1a7834?crop=entropy&cs=srgb&fm=jpg&q=85",
      category: "Software Solution",
      title: "Enterprise CRM",
      description: "Custom relationship management system",
      url: null,
      featured: false,
    },
    {
      image: "https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=srgb&fm=jpg&q=85",
      category: "IT Consulting",
      title: "Cloud Migration",
      description: "Infrastructure modernization project",
      url: null,
      featured: false,
    },
    {
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85",
      category: "Web Development",
      title: "E-Commerce Platform",
      description: "Complete digital shopping experience with inventory management",
      url: null,
      featured: false,
    },
    {
      image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?crop=entropy&cs=srgb&fm=jpg&q=85",
      category: "Software Solution",
      title: "IoT Management System",
      description: "Device monitoring and analytics platform",
      url: null,
      featured: false,
    },
  ];

  return (
    <section id="portfolio" className="py-32 lg:py-48 bg-white">
      <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24">
        <AnimatedSection className="mb-16">
          <p className="section-subtitle">Our Work</p>
          <h2 className="section-title font-display">
            Selected Projects
            <br />
            <span className="font-accent font-normal italic">& Case Studies</span>
          </h2>
        </AnimatedSection>

        {/* Featured Project */}
        {projects.filter(p => p.featured).map((project, index) => (
          <AnimatedSection key={`featured-${index}`} delay={0.1}>
            <a 
              href={project.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block mb-12 group"
              data-testid="featured-project"
            >
              <div className="relative aspect-[21/9] overflow-hidden bg-ash">
                <img 
                  src={project.image} 
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-onyx/0 group-hover:bg-onyx/60 transition-colors duration-500 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-500 text-center text-vapor">
                    <p className="text-xs uppercase tracking-widest mb-3 opacity-80">{project.category}</p>
                    <h3 className="font-display text-4xl font-bold mb-2">{project.title}</h3>
                    <p className="text-lg opacity-80 mb-4">{project.description}</p>
                    <span className="inline-flex items-center gap-2 text-sm uppercase tracking-widest">
                      View Live <ExternalLink size={14} strokeWidth={1.5} />
                    </span>
                  </div>
                </div>
                {/* Featured Badge */}
                <div className="absolute top-4 left-4 bg-onyx text-vapor px-4 py-2 text-xs uppercase tracking-widest font-bold">
                  Featured Product
                </div>
              </div>
            </a>
          </AnimatedSection>
        ))}

        {/* Other Projects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.filter(p => !p.featured).map((project, index) => (
            <AnimatedSection key={index} delay={index * 0.1}>
              <div className="portfolio-item" data-testid={`portfolio-item-${index}`}>
                <img src={project.image} alt={project.title} />
                <div className="portfolio-overlay">
                  <div className="portfolio-content">
                    <p className="text-xs uppercase tracking-widest mb-2 opacity-80">{project.category}</p>
                    <h3 className="font-display text-xl font-bold mb-1">{project.title}</h3>
                    <p className="text-sm opacity-80">{project.description}</p>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", company: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-32 lg:py-48 bg-ash">
      <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Content */}
          <AnimatedSection>
            <p className="section-subtitle">Get in Touch</p>
            <h2 className="section-title font-display">
              Let's Build
              <br />
              <span className="font-accent font-normal italic">Something Great</span>
            </h2>
            
            <p className="text-slate text-lg leading-relaxed mb-12 max-w-md">
              Ready to transform your digital presence? We'd love to hear about 
              your project and explore how we can help bring your vision to life.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Mail size={20} strokeWidth={1} className="text-charcoal" />
                <a href="mailto:info@jerves.net" className="text-slate hover:text-onyx transition-colors" data-testid="contact-email">
                  info@jerves.net
                </a>
              </div>
              <div className="flex items-center gap-4">
                <Phone size={20} strokeWidth={1} className="text-charcoal" />
                <a href="tel:+1234567890" className="text-slate hover:text-onyx transition-colors" data-testid="contact-phone">
                  +1 (234) 567-890
                </a>
              </div>
              <div className="flex items-center gap-4">
                <MapPin size={20} strokeWidth={1} className="text-charcoal" />
                <span className="text-slate" data-testid="contact-address">
                  Global Remote Team
                </span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex gap-6 mt-12">
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-slate hover:text-onyx transition-colors" data-testid="social-linkedin">
                <Linkedin size={24} strokeWidth={1} />
              </a>
              <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="text-slate hover:text-onyx transition-colors" data-testid="social-github">
                <Github size={24} strokeWidth={1} />
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-slate hover:text-onyx transition-colors" data-testid="social-twitter">
                <Twitter size={24} strokeWidth={1} />
              </a>
            </div>
          </AnimatedSection>

          {/* Form */}
          <AnimatedSection delay={0.2}>
            <form onSubmit={handleSubmit} className="space-y-8 bg-white p-8 lg:p-12" data-testid="contact-form">
              <div>
                <input
                  type="text"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="form-input"
                  required
                  data-testid="input-name"
                />
              </div>
              <div>
                <input
                  type="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="form-input"
                  required
                  data-testid="input-email"
                />
              </div>
              <div>
                <input
                  type="text"
                  placeholder="Company (Optional)"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="form-input"
                  data-testid="input-company"
                />
              </div>
              <div>
                <textarea
                  placeholder="Tell us about your project"
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="form-input resize-none"
                  required
                  data-testid="input-message"
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full flex items-center justify-center gap-3 disabled:opacity-50"
                data-testid="submit-contact"
              >
                <span>{isSubmitting ? "Sending..." : "Send Message"}</span>
                <ArrowRight size={16} strokeWidth={1.5} />
              </button>
            </form>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="py-16 bg-onyx text-vapor">
      <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-16">
          {/* Brand */}
          <div className="lg:col-span-1">
            <h3 className="font-display text-2xl font-bold mb-4">JERVES.NET</h3>
            <p className="text-vapor/60 max-w-xs leading-relaxed text-sm">
              Crafting premium digital experiences for visionary businesses. 
              We transform ideas into elegant, powerful software solutions.
            </p>
          </div>

          {/* Navigation Links */}
          <div>
            <h4 className="text-xs uppercase tracking-widest mb-6 text-vapor/40">Navigation</h4>
            <div className="space-y-3">
              <button onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })} className="footer-link block text-vapor/60 hover:text-vapor text-sm" data-testid="footer-services">
                Services
              </button>
              <button onClick={() => document.getElementById("products")?.scrollIntoView({ behavior: "smooth" })} className="footer-link block text-vapor/60 hover:text-vapor text-sm" data-testid="footer-products">
                Products
              </button>
              <button onClick={() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth" })} className="footer-link block text-vapor/60 hover:text-vapor text-sm" data-testid="footer-about">
                About
              </button>
              <button onClick={() => document.getElementById("portfolio")?.scrollIntoView({ behavior: "smooth" })} className="footer-link block text-vapor/60 hover:text-vapor text-sm" data-testid="footer-work">
                Work
              </button>
            </div>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-xs uppercase tracking-widest mb-6 text-vapor/40">Products</h4>
            <div className="space-y-3">
              <a href="https://project.jarves.net" target="_blank" rel="noopener noreferrer" className="footer-link flex items-center gap-2 text-vapor/60 hover:text-vapor text-sm" data-testid="footer-jarves-project">
                Jarves Project <ExternalLink size={12} strokeWidth={1.5} />
              </a>
              <span className="block text-vapor/40 text-sm italic">More coming soon...</span>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-xs uppercase tracking-widest mb-6 text-vapor/40">Contact</h4>
            <div className="space-y-3">
              <a href="mailto:info@jerves.net" className="footer-link block text-vapor/60 hover:text-vapor text-sm">
                info@jerves.net
              </a>
              <a href="tel:+1234567890" className="footer-link block text-vapor/60 hover:text-vapor text-sm">
                +1 (234) 567-890
              </a>
            </div>
            {/* Social Links */}
            <div className="flex gap-4 mt-6">
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-vapor/40 hover:text-vapor transition-colors">
                <Linkedin size={18} strokeWidth={1} />
              </a>
              <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="text-vapor/40 hover:text-vapor transition-colors">
                <Github size={18} strokeWidth={1} />
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-vapor/40 hover:text-vapor transition-colors">
                <Twitter size={18} strokeWidth={1} />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-vapor/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-vapor/40">
            © {currentYear} Jerves.net. All rights reserved.
          </p>
          <p className="text-sm text-vapor/40">
            Crafted with precision
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main Home Page
const Home = () => {
  return (
    <div className="min-h-screen">
      <Navigation />
      <HeroSection />
      <ServicesSection />
      <ProductsSection />
      <AboutSection />
      <PortfolioSection />
      <ContactSection />
      <Footer />
      <Toaster position="bottom-right" />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
